import {useDispatch} from 'react-redux';
import {DirectionType, Feedback, TaskType} from 'constants/dataEnum';
import PipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileWrapper';
import {
  setTaskEnableMixing,
  setTaskMixingCycle,
  setTaskMixingPositionAspirate,
  setTaskMixingPositionDispense,
  setTaskMixingSpeed,
  setTaskMixingVolume,
  setTaskPrecisionDispense,
  setTaskRetractSpeed,
  setTaskSpeed,
  setTaskTipPosition,
  setTaskTipTouchOff,
  setTaskTipTouchOffDelay,
  setTaskTipTouchOffDirectionX,
  setTaskTipTouchOffDirectionY,
  setTaskTipTouchOffHeight,
} from 'stores/actions/cardStateAction';
import PipettingProfileItems from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileItem/PipettingProfileItems';
import {copyTaskAllCards, copyTaskSameCard, getCopyPipettingValueMode} from 'stores/business/taskListBusinessAction';
import PropTypes from 'prop-types';
import {parseMessage} from 'lib/stringHelper';
import {updateRunningProtocol} from 'stores/business/protocolManagementBusiness';
import {showSnackbar} from 'stores/actions/appStateAction';
import {CopyPipettingValueMode} from 'constants/cardStateEnum';
import {useState} from 'react';
import PipettingProfileExpanderButton from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileExpanderButton';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import './SimpleTransferPipettingProfile.scss';
import {
  showApplyPipettingConfirmationModal,
  updatePipettingTipPositionOffset,
} from 'stores/business/pipettingParameterBusiness';
import {useIntl} from 'react-intl';
import {checkTipVolume} from 'stores/business/aspirateDispenseVolumeModalBusiness';

function SimpleTransferPipettingProfile({task, runtimeMode, card}) {
  const [isPipettingProfileExpanded, setIsPipettingProfileExpanded] = useState(false);
  const [liquidInTipsError, setLiquidInTipsError] = useState(false);

  const {type: taskType, ID: taskId, volume: taskVolume} = task || {};

  const dispatch = useDispatch();
  const intl = useIntl();

  const copyPipettingValueMode = dispatch(getCopyPipettingValueMode(task));

  const postUpdateAction = () => {
    if (runtimeMode) {
      dispatch(updateRunningProtocol());
    } else {
      dispatch(globalAddUndoItem());
    }
  };

  const updatePipettingValue = (updateFunction, value) => {
    dispatch(updateFunction(taskId, value));

    postUpdateAction();
  };

  const updatePipettingTipPositionOffsetValue = (direction, value) => {
    dispatch(updatePipettingTipPositionOffset(direction, value, task));

    postUpdateAction();
  };

  const copyTaskButtonOnClick = async () => {
    const callback = () => {
      const result = dispatch(copyTaskSameCard(taskId, runtimeMode));

      if (!result) return;

      dispatch(
        showSnackbar(
          Feedback.INFO,
          parseMessage('protocolEditor.taskList.pipettingProfile.copyTaskNotification', {taskType}),
        ),
      );
    };

    dispatch(showApplyPipettingConfirmationModal(CopyPipettingValueMode.TO_ALL_TASKS, callback));
  };

  const copyTaskToAllButtonOnClick = () => {
    const callback = () => {
      const result = dispatch(copyTaskAllCards(taskId, runtimeMode));

      if (!result) return;

      dispatch(
        showSnackbar(
          Feedback.INFO,
          parseMessage('protocolEditor.taskList.pipettingProfile.copyTaskToAllNotification', {taskType}),
        ),
      );
    };

    dispatch(showApplyPipettingConfirmationModal(CopyPipettingValueMode.TO_ALL_CARDS, callback));
  };

  const setTipPosition = (value) => updatePipettingValue(setTaskTipPosition, value);
  const setTipXPosition = (value) => updatePipettingTipPositionOffsetValue(DirectionType.DIRECTION_X, value);
  const setTipYPosition = (value) => updatePipettingTipPositionOffsetValue(DirectionType.DIRECTION_Y, value);
  const setSpeed = (value) => updatePipettingValue(setTaskSpeed, value);
  const setRetractSpeed = (value) => updatePipettingValue(setTaskRetractSpeed, value);
  const setEnableMixing = (value) => {
    dispatch(setTaskEnableMixing(taskId, value));
    setTimeout(() => {
      // If new value is false then we don't need to check liquid in tip
      if (!value) {
        postUpdateAction();
        setLiquidInTipsError(false);
        return;
      }
      const [tipVolume] = dispatch(checkTipVolume(card, task));
      let isEnableMixingAllowed = true;
      if (taskType === TaskType.DISPENSE) {
        isEnableMixingAllowed = tipVolume <= taskVolume;
      } else {
        isEnableMixingAllowed = tipVolume === 0;
      }
      if (isEnableMixingAllowed) {
        postUpdateAction();
        setLiquidInTipsError(false);
      } else {
        dispatch(setTaskEnableMixing(taskId, false));
        setLiquidInTipsError(true);
      }
    }, 200);
  };
  const setMixingVolume = (value) => updatePipettingValue(setTaskMixingVolume, value);
  const setMixingCycle = (value) => updatePipettingValue(setTaskMixingCycle, value);
  const setMixingPositionAspirate = (value) => updatePipettingValue(setTaskMixingPositionAspirate, value);
  const setMixingPositionDispense = (value) => updatePipettingValue(setTaskMixingPositionDispense, value);
  const setMixingSpeed = (value) => updatePipettingValue(setTaskMixingSpeed, value);
  const setPrecisionDispense = (value) => updatePipettingValue(setTaskPrecisionDispense, value);
  const setTipTouchOffDelay = (value) => updatePipettingValue(setTaskTipTouchOffDelay, value);
  const setTipTouchOffHeight = (value) => updatePipettingValue(setTaskTipTouchOffHeight, value);
  const setTipTouchOffDirectionX = (value) => updatePipettingValue(setTaskTipTouchOffDirectionX, value);
  const setTipTouchOffDirectionY = (value) => updatePipettingValue(setTaskTipTouchOffDirectionY, value);

  const setEnableTipTouchOff = (value) => {
    updatePipettingValue(setTaskTipTouchOff, value);
  };

  return (
    <div className="simple-transfer-pipetting-profile">
      {isPipettingProfileExpanded && (
        <PipettingProfileWrapper>
          <PipettingProfileItems.TipPosition
            task={task}
            onChange={setTipPosition}
            onXTipPositionOnChange={setTipXPosition}
            onYTipPositionOnChange={setTipYPosition}
          />
          {taskType === TaskType.DISPENSE && (
            <PipettingProfileItems.PrecisionDispense task={task} onChange={setPrecisionDispense} />
          )}
          <PipettingProfileItems.Speed task={task} onChange={setSpeed} />
          <PipettingProfileItems.RetractSpeed task={task} onChange={setRetractSpeed} />
          <PipettingProfileItems.EnableMixing
            task={task}
            onChange={setEnableMixing}
            mixingError={liquidInTipsError}
            errorTooltip={intl.formatMessage({
              id: 'protocolEditor.taskList.enableMixingAfterDispense.liquidInTipBeforeMixing',
            })}
          />
          <PipettingProfileItems.MixingDetailGroup
            task={task}
            onChangeVolume={setMixingVolume}
            onChangeCycle={setMixingCycle}
            onChangeMixingPositionAspirate={setMixingPositionAspirate}
            onChangeMixingPositionDispense={setMixingPositionDispense}
            onChangeMixingSpeed={setMixingSpeed}
          />
          <PipettingProfileItems.TipTouchOff
            task={task}
            onChangeSwitch={setEnableTipTouchOff}
            onChangeDelay={setTipTouchOffDelay}
            onChangeHeight={setTipTouchOffHeight}
            onChangeDirectionX={setTipTouchOffDirectionX}
            onChangeDirectionY={setTipTouchOffDirectionY}
          />
          {copyPipettingValueMode !== CopyPipettingValueMode.NONE && (
            <PipettingProfileItems.CopyPipettingValueButton
              copyPipettingValueMode={copyPipettingValueMode}
              onClickAllTasks={copyTaskButtonOnClick}
              onClickAllCards={copyTaskToAllButtonOnClick}
            />
          )}
        </PipettingProfileWrapper>
      )}
      <PipettingProfileExpanderButton isExpanded={isPipettingProfileExpanded} onClick={setIsPipettingProfileExpanded} />
    </div>
  );
}

SimpleTransferPipettingProfile.propTypes = {
  task: PropTypes.object.isRequired,
  card: PropTypes.object.isRequired,
  runtimeMode: PropTypes.bool,
};

SimpleTransferPipettingProfile.defaultProps = {
  runtimeMode: false,
};

export default SimpleTransferPipettingProfile;
